import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { useAppContext, useAuthContext } from '@/hooks'
import {
	useActivateBranchMutation,
	useArchiveBranchMutation,
	useDeleteBranchMutation,
	useLockBranchMutation,
	useUnlockBranchMutation,
	useUpdateBranchMutation,
	useUpdateDeploymentPackageMutation,
} from '@/rtkApi'
import { BranchDto, BranchStatusEnum } from '@/rtkApi/mddApiSliceGeneratedV2'
import {
	getApiErrorDescription,
	logErrorAndNotifyWithToast,
} from '@/utils/errorHandling'

import { toBranchUpdateDto } from '../mapBranch'
import { BranchRecord } from '../types'

export const useBranchActions = () => {
	const { t } = useAppContext()
	const auth = useAuthContext()
	const [updateBranch] = useUpdateBranchMutation()
	const [deleteBranch] = useDeleteBranchMutation()
	const [activateBranch] = useActivateBranchMutation()
	const [archiveBranch] = useArchiveBranchMutation()
	const [lockBranch] = useLockBranchMutation()
	const [unlockBranch] = useUnlockBranchMutation()
	const [updateDeploymentPackage] = useUpdateDeploymentPackageMutation()
	const [updatingDeploymentPackageId, setUpdatingDeploymentPackageId] = useState<
		string | null
	>(null)
	const canManage = auth.isAdmin() || auth.canCreateSystem()

	const patchBranch = useCallback(
		async (branch: BranchRecord, patch: Partial<BranchDto>) => {
			try {
				await updateBranch({
					branchId: Number(branch.id),
					branchDto: toBranchUpdateDto(branch, patch),
				}).unwrap()
			} catch (error) {
				logErrorAndNotifyWithToast({
					context: { operation: 'useBranchActions.patchBranch' },
					detail: getApiErrorDescription(error),
					error,
					messageKey: 'BRANCHES_UPDATE_FAILED',
					t,
				})
			}
		},
		[t, updateBranch],
	)

	const setStatus = useCallback(
		async (branch: BranchRecord, status: BranchStatusEnum) => {
			try {
				const branchId = Number(branch.id)
				if (status === 'LOCKED') {
					await lockBranch({ branchId }).unwrap()
				} else if (status === 'ARCHIVED') {
					await archiveBranch({ branchId }).unwrap()
				} else if (branch.status === 'LOCKED') {
					await unlockBranch({ branchId }).unwrap()
				} else {
					await activateBranch({ branchId }).unwrap()
				}
			} catch (error) {
				logErrorAndNotifyWithToast({
					context: { operation: 'useBranchActions.setStatus' },
					detail: getApiErrorDescription(error),
					error,
					messageKey: 'BRANCHES_UPDATE_FAILED',
					t,
				})
			}
		},
		[activateBranch, archiveBranch, lockBranch, t, unlockBranch],
	)

	const toggleProtected = useCallback(
		(branch: BranchRecord) =>
			patchBranch(branch, { protectedBranch: !branch.protectedBranch }),
		[patchBranch],
	)

	const removeBranch = useCallback(
		async (branch: BranchRecord) => {
			try {
				await deleteBranch({ branchId: Number(branch.id) }).unwrap()
				return true
			} catch (error) {
				logErrorAndNotifyWithToast({
					context: { operation: 'useBranchActions.removeBranch' },
					detail: getApiErrorDescription(error),
					error,
					messageKey: 'BRANCHES_DELETE_FAILED',
					t,
				})
				return false
			}
		},
		[deleteBranch, t],
	)

	const refreshDeploymentPackage = useCallback(
		async (branch: BranchRecord) => {
			setUpdatingDeploymentPackageId(branch.id)
			try {
				await updateDeploymentPackage({
					branchId: Number(branch.id),
				}).unwrap()
				toast(t('BRANCHES_UPDATE_DEPLOYMENT_PACKAGE_SUCCESS'), {
					type: 'success',
				})
			} catch (error) {
				logErrorAndNotifyWithToast({
					context: { operation: 'useBranchActions.refreshDeploymentPackage' },
					detail: getApiErrorDescription(error),
					error,
					messageKey: 'BRANCHES_UPDATE_DEPLOYMENT_PACKAGE_FAILED',
					t,
				})
			} finally {
				setUpdatingDeploymentPackageId(null)
			}
		},
		[t, updateDeploymentPackage],
	)

	return {
		canManage,
		refreshDeploymentPackage,
		removeBranch,
		setStatus,
		toggleProtected,
		updatingDeploymentPackageId,
	}
}
